import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';

import DonutChart from '@/components/charts/DonutChart/DonutChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import { calcChangePercentage } from '@/pages/Dashboard/Dashboard.utils';
import { organizationsAtom } from '@/pages/Organizations';
import { REPORTS_SERVICE_TICKETS_HORIZONTAL_BAR_SERIES } from '@/pages/ReportsTemplates/ReportsTemplates.constants';
import type { Ticket } from '@/pages/Tickets';
import { axiosClient } from '@/services/BaseApi.service';

import {
  reportsEndDateAtom,
  reportsOrgLabelAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import useStyles from '../ReportsPages.styles';
import {
  formatServiceTicketCategoryGroup,
  formatServiceTicketStatusGroup,
  ticketsFilledBox
} from './TicketsPages.utils';

const TicketsPage3 = () => {
  const { classes } = useStyles();
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const [diff] = useAtom(reportsTimeFrameAtom);
  const [orgLabel] = useAtom(reportsOrgLabelAtom);
  const [organizations] = useAtom(organizationsAtom);

  const [customerServiceTickets, setCustomerServiceTickets] = useState<
    Ticket[]
  >([]);
  const [ponduranceServiceTickets, setPonduranceServiceTickets] = useState<
    Ticket[]
  >([]);

  const [
    previousPonduranceServiceTickets,
    setPreviousPonduranceServiceTickets
  ] = useState<Ticket[]>([]);
  const [previousCustomerServiceTickets, setPreviousCustomerServiceTickets] =
    useState<Ticket[]>([]);

  const organization = organizations.find((org) => org.label === orgLabel);

  useEffect(() => {
    if (!organization?.id) return;
    const getServiceTickets = async () => {
      const customerResponse = await axiosClient.get(
        `organizations/${organization?.id}/tickets/group_by_user_type`,
        {
          params: {
            user_type: 'customer',
            start_date: dayjs(startDate).format('YYYY-MM-DD'),
            end_date: dayjs(endDate).format('YYYY-MM-DD'),
            ticket_type: 'service'
          }
        }
      );
      setCustomerServiceTickets(customerResponse.data || []);

      const ponduranceResponse = await axiosClient.get(
        `organizations/${organization?.id}/tickets/group_by_user_type`,
        {
          params: {
            user_type: 'internal',
            start_date: dayjs(startDate).format('YYYY-MM-DD'),
            end_date: dayjs(endDate).format('YYYY-MM-DD'),
            ticket_type: 'service'
          }
        }
      );
      setPonduranceServiceTickets(ponduranceResponse.data || []);

      const previousCustomerResponse = await axiosClient.get(
        `organizations/${organization?.id}/tickets/group_by_user_type`,
        {
          params: {
            user_type: 'customer',
            start_date: dayjs(startDate)
              .subtract(diff, 'days')
              .format('YYYY-MM-DD'),
            end_date: dayjs(endDate)
              .subtract(diff, 'days')
              .format('YYYY-MM-DD'),
            ticket_type: 'service'
          }
        }
      );
      setPreviousCustomerServiceTickets(previousCustomerResponse.data || []);

      const previousPonduranceResponse = await axiosClient.get(
        `organizations/${organization?.id}/tickets/group_by_user_type`,
        {
          params: {
            user_type: 'internal',
            start_date: dayjs(startDate)
              .subtract(diff, 'days')
              .format('YYYY-MM-DD'),
            end_date: dayjs(endDate)
              .subtract(diff, 'days')
              .format('YYYY-MM-DD'),
            ticket_type: 'service'
          }
        }
      );
      setPreviousPonduranceServiceTickets(
        previousPonduranceResponse.data || []
      );
    };
    getServiceTickets();
  }, [startDate, endDate, organization?.id]);

  const customerServiceTicketsData = [
    {
      id: 1,
      title: 'New',
      value: customerServiceTickets.length,
      change: calcChangePercentage(
        customerServiceTickets.length,
        previousCustomerServiceTickets.length
      ).toFixed(2)
    },
    {
      id: 2,
      title: 'Unresolved',
      value: customerServiceTickets.filter(
        (ticket: Ticket) =>
          ticket.ticket_status?.name !== 'Completed' &&
          ticket.ticket_status?.name !== 'Auto Closed'
      ).length,
      change: calcChangePercentage(
        customerServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name !== 'Completed' &&
            ticket.ticket_status?.name !== 'Auto Closed'
        ).length,
        previousCustomerServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name !== 'Completed' &&
            ticket.ticket_status?.name !== 'Auto Closed'
        ).length
      ).toFixed(2)
    },
    {
      id: 3,
      title: 'Resolved',
      value: customerServiceTickets.filter(
        (ticket: Ticket) =>
          ticket.ticket_status?.name === 'Completed' ||
          ticket.ticket_status?.name === 'Auto Closed'
      ).length,
      change: calcChangePercentage(
        customerServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name === 'Completed' ||
            ticket.ticket_status?.name === 'Auto Closed'
        ).length,
        previousCustomerServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name === 'Completed' ||
            ticket.ticket_status?.name === 'Auto Closed'
        ).length
      ).toFixed(2)
    }
  ];

  const ponduranceServiceTicketsData = [
    {
      id: 4,
      title: 'New',
      value: ponduranceServiceTickets.length,
      change: calcChangePercentage(
        ponduranceServiceTickets.length,
        previousPonduranceServiceTickets.length
      ).toFixed(2)
    },
    {
      id: 5,
      title: 'Unresolved',
      value: ponduranceServiceTickets.filter(
        (ticket: Ticket) =>
          ticket.ticket_status?.name !== 'Completed' &&
          ticket.ticket_status?.name !== 'Auto Closed'
      ).length,
      change: calcChangePercentage(
        ponduranceServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name !== 'Completed' &&
            ticket.ticket_status?.name !== 'Auto Closed'
        ).length,
        previousPonduranceServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name !== 'Completed' &&
            ticket.ticket_status?.name !== 'Auto Closed'
        ).length
      ).toFixed(2)
    },
    {
      id: 6,
      title: 'Resolved',
      value: ponduranceServiceTickets.filter(
        (ticket: Ticket) =>
          ticket.ticket_status?.name === 'Completed' ||
          ticket.ticket_status?.name === 'Auto Closed'
      ).length,
      change: calcChangePercentage(
        ponduranceServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name === 'Completed' ||
            ticket.ticket_status?.name === 'Auto Closed'
        ).length,
        previousPonduranceServiceTickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_status?.name === 'Completed' ||
            ticket.ticket_status?.name === 'Auto Closed'
        ).length
      ).toFixed(2)
    }
  ];

  return (
    <div className={classes.reportsPageContainer}>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.serviceTicketsDescription}
      </ReportParagraph>
      <Stack direction="row" spacing={2} sx={{ height: '100%', width: '100%' }}>
        <Stack
          spacing={3}
          className={classes.serviceTicketColumn}
          direction="column"
        >
          <div
            data-testid="service-tickets-filed-by-you"
            style={{ height: '300' }}
          >
            <ReportsTemplateBoxContainer>
              {ticketsFilledBox(
                customerServiceTicketsData,
                'Service Tickets Filed by You'
              )}
            </ReportsTemplateBoxContainer>
          </div>
          <Stack direction="column" spacing={1}>
            <div
              data-testid="top-service-ticket-categories-filed-by-you"
              style={{ height: '200px' }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Service Ticket Categories
              </Typography>
              <HorizontalBarChart
                height={165}
                data={formatServiceTicketCategoryGroup(customerServiceTickets)}
                series={REPORTS_SERVICE_TICKETS_HORIZONTAL_BAR_SERIES}
              />
            </div>

            <Typography
              sx={{
                fontSize: 10,
                marginTop: '-15px !important',
                height: '108px'
              }}
            >
              {REPORT_PARAGRAPHS.serviceTicketsByCustomerCategoryDescription}
            </Typography>
          </Stack>
          <Stack direction="column" spacing={1}>
            <div
              data-testid="service-tickets-by-status-filed-by-you"
              style={{ height: '180px' }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                SERVICE TICKETS BY STATUS
              </Typography>
              <DonutChart
                data={formatServiceTicketStatusGroup(
                  customerServiceTickets
                ).map((item) => ({
                  name: item.name,
                  count: Array.isArray(item.data) ? item.data[0] || 0 : 0,
                  color: item.color
                }))}
                resolveCssVariables
              />
            </div>
            <Typography
              sx={{
                fontSize: 10,
                position: 'relative',
                marginTop: '0.75rem !important',
                zIndex: 2
              }}
            >
              {REPORT_PARAGRAPHS.serviceTicketsByStatusCustomerDescription}
            </Typography>
          </Stack>
        </Stack>
        <Box
          sx={{
            backgroundColor: '#D1E2D5',
            width: 2,
            height: '100%'
          }}
        />
        <Stack
          spacing={3}
          className={classes.serviceTicketColumn}
          direction="column"
        >
          <div style={{ height: '300' }}>
            <ReportsTemplateBoxContainer>
              {ticketsFilledBox(
                ponduranceServiceTicketsData,
                'Service Tickets Filed by Pondurance'
              )}
            </ReportsTemplateBoxContainer>
          </div>
          <Stack direction="column" spacing={1}>
            <div
              data-testid="top-service-ticket-categories-filed-by-pondurance"
              style={{ height: '200px' }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                Top Service Ticket Categories
              </Typography>
              <HorizontalBarChart
                height={165}
                data={formatServiceTicketCategoryGroup(
                  ponduranceServiceTickets
                )}
                series={REPORTS_SERVICE_TICKETS_HORIZONTAL_BAR_SERIES}
              />
            </div>
            <Typography
              sx={{
                fontSize: 10,
                marginTop: '-15px !important',
                height: '108px'
              }}
            >
              {REPORT_PARAGRAPHS.serviceTicketsByPonduranceCategoryDescription}
            </Typography>
          </Stack>
          <Stack direction="column" spacing={1}>
            <div
              data-testid="service-tickets-by-status-filed-by-pondurance"
              style={{ height: '180px' }}
            >
              <Typography className={classes.reportsPreviewSubTitle}>
                SERVICE TICKETS BY STATUS
              </Typography>
              <DonutChart
                data={formatServiceTicketStatusGroup(
                  ponduranceServiceTickets
                ).map((item) => ({
                  name: item.name,
                  count: Array.isArray(item.data) ? item.data[0] || 0 : 0,
                  color: item.color
                }))}
                resolveCssVariables
              />
            </div>
            <Typography
              sx={{
                fontSize: 10,
                marginTop: '0.75rem !important',
                position: 'relative',
                zIndex: 2
              }}
            >
              {REPORT_PARAGRAPHS.serviceTicketsByStatusPonduranceDescription}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

export default TicketsPage3;
